import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import {Alert, Button, Tooltip, Typography, useMediaQuery} from '@mui/material';
import {Box, Container, Stack} from '@mui/system';
import {OTPSplitDigitInput} from '../../../components/forms/OTPSplitDigitInput';
import {mainLinearGradient} from '../../../theme/gradients';
import {AuthHeading, AuthLogo} from '../base';

/**
 * Component for verifying the user's email.
 *
 * @param {Object} props - The component properties.
 * @param {string} props.code - The verification code.
 * @param {Function} props.setCode - Function to set the verification code.
 * @param {boolean} props.incorrect - Indicates if the verification code is incorrect.
 * @param {string} props.maskedEmail - The masked email to display.
 * @param {Function} props.handleVerify - Function to handle the verification process.
 * @param {Function} props.handleSendVerify - Function to handle sending a new verification code.
 * @param {boolean} props.sendNewCodeActivated - Indicates if sending a new code is activated.
 *
 * @returns {JSX.Element} - The rendered VerifyEmailPageJSX component.
 */
export const VerifyEmailPageJSX = props => {
  const {code, setCode, incorrect, maskedEmail, handleVerify, handleSendVerify, sendNewCodeActivated} = props;
  const isXSSmallScreen = useMediaQuery('@media (max-width:375px)');

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} alignItems="start" justifyContent="center" sx={{height: '100vh'}}>
        <AuthLogo />
        <AuthHeading>Verify your Email</AuthHeading>
        <Typography color="darkgray">
          Please check your email box for a verification code to confirm your identity. The code may take up to 2
          minutes to be delivered. Don't forget to check your spam folder as well.
        </Typography>
        {incorrect ? (
          <Alert
            severity="error"
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              marginX: 'auto',
            }}
          >
            Sorry, please try again.
          </Alert>
        ) : (
          ''
        )}
        <Box
          sx={{
            bgcolor: 'grayscale.light',
            border: '1px solid lightgray',
            borderRadius: 5,
            p: 3,
            boxSizing: 'border-box',
            width: 1,
          }}
        >
          <Stack>
            <Typography color="darkgray" sx={{mb: 1}}>
              A code was sent to <span style={{color: '#35a7ee', fontWeight: 'bold'}}>{maskedEmail}</span>
            </Typography>
            <OTPSplitDigitInput code={code} setCode={setCode} />
            <Button
              onClick={handleVerify}
              variant="contained"
              disableElevation
              size="large"
              sx={{color: 'white', background: mainLinearGradient, mt: 2}}
              endIcon={<ArrowForwardIcon />}
            >
              Verify
            </Button>
          </Stack>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            color: 'darkgray',
            width: 1,
            gap: isXSSmallScreen ? 6 : undefined,
          }}
        >
          <Tooltip
            title={
              !sendNewCodeActivated
                ? 'Please wait for one minute after requesting a new code before using this button again.'
                : ''
            }
          >
            <span>
              <Button
                onClick={handleSendVerify}
                startIcon={<RestartAltIcon />}
                disabled={!sendNewCodeActivated}
                sx={{textAlign: 'left'}}
              >
                Send a new code
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Container>
  );
};
